import {
  IncomeEvaluation,
  isTaxIncomeFlag,
} from '@/apis/family_application/requests'
import { buttonVariants } from '@/app/common/components/Button'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import LoadingCard from '@/app/common/components/LoadingCard'
import { CheckMarkIcon, TimeIcon } from '@/app/common/icons/arrows'
import { AppRoutePaths } from '@/routing/route_paths'
import {
  RefreshFamilyApplication,
  selectFamilyApplication,
} from '@/storage/applications/familyApplicationSlice'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { AppDispatch, RootState } from '@/storage/store'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import FamilyWizardLayout from '../../FamilyWizardLayout'
import TaxCommissionSummary from './TaxCommissionSummary'

function SummaryPage() {
  const { t } = useTranslation()
  const familyApplication = useSelector(selectFamilyApplication)
  const {
    request: { status },
    tax_commission_response,
  } = useSelector((state: RootState) => state.FamilyApplication)

  const dispatch = useDispatch<AppDispatch>()
  const hasIncomeFlags = familyApplication.flags.some(
    (flag) => isTaxIncomeFlag(flag.flag_code) && flag.status !== 'Resolved'
  )
  const programId = useSelector(selectProgramID)

  useEffect(() => {
    dispatch(RefreshFamilyApplication(programId))
  }, [])

  function getEvaluationMethod(method: IncomeEvaluation | null) {
    if (method === 'manual') {
      return 'Manual'
    }

    if (method === 'tax_commission') {
      return 'Tax Commission'
    }

    if (method === 'dws') {
      return 'DWS'
    }

    return ''
  }

  if (status === 'in_flight') {
    return (
      <LoadingCard isLoading={true}>
        <h1>{t('loading')}...</h1>
      </LoadingCard>
    )
  }

  const incomeFormatter = new Intl.NumberFormat('en-US', {
    currency: 'USD',
    style: 'currency',
  })

  function getTotalIncome() {
    if (!familyApplication.verified_feed_adjustment_gross_income) {
      return null
    } else {
      const income = incomeFormatter.format(
        familyApplication.verified_feed_adjustment_gross_income
      )
      return income
    }
  }

  const totalIncome = getTotalIncome()
  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{t('income_summary_verification')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content>
        {hasIncomeFlags ? (
          <TimeIcon className="text-6xl text-amber-600 mx-auto" />
        ) : (
          <CheckMarkIcon className="text-green-800 text-6xl mx-auto" />
        )}
        <p className="text-center text-gray-700 mb-10">
          {t('thanks_for_providing_income_info')}{' '}
          {hasIncomeFlags && t('your_income_under_review')}
        </p>
        <Card>
          <CardHeader className="flex items-center justify-between px-2">
            <h2 className="font-medium">{t('information')}</h2>
            <Link
              className={buttonVariants({
                variant: 'outline',
              })}
              to={AppRoutePaths.family_application.income_verification.index}
            >
              {t('edit_income_details')}
            </Link>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="font-normal text-gray-800">
                {t('verification_method')}
              </div>
              <div className="text-gray-700  text-sm font-medium">
                {t(getEvaluationMethod(familyApplication.income_evaluated_by))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="font-normal text-gray-800">
                {t('household_members')}
              </div>
              <div className="text-gray-700 text-sm font-medium ">
                {familyApplication.household_inhabitants}
              </div>
            </div>
            {familyApplication.household_filed_federal_income && (
              <div className="flex items-center justify-between">
                <div className="font-normal text-gray-800">
                  {t('federal_filing_type')}
                </div>
                <div className="text-gray-700 capitalize  text-sm font-medium ">
                  {t(familyApplication.household_filed_federal_income)}
                </div>
              </div>
            )}
            {totalIncome && (
              <div className="flex items-center justify-between">
                <div className="font-normal text-gray-800">{t('Income')}</div>
                <div className="text-gray-700 capitalize  text-sm font-medium ">
                  {totalIncome}
                </div>
              </div>
            )}
            {familyApplication.household_filed_federal_income ==
              'individually' && (
              <div className="flex items-center justify-between">
                <div className="font-normal text-gray-800">
                  {t('spouse_income')}
                </div>
                <div className="text-gray-700 capitalize text-sm font-medium ">
                  {incomeFormatter.format(
                    familyApplication.spouse_verified_gross_income ?? 0
                  )}
                </div>
              </div>
            )}

            <TaxCommissionSummary
              taxCommissionInfo={tax_commission_response}
              application={familyApplication}
            />
          </CardContent>
        </Card>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar>
        <Link
          className={buttonVariants({
            variant: 'primary',
            className: 'w-full',
          })}
          to={
            hasIncomeFlags
              ? AppRoutePaths.family_application.review_application
              : AppRoutePaths.family_application.income_verification.completed
          }
        >
          {t('continue')}
        </Link>
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}

export default SummaryPage
